/**
 * Note List Component
 *
 * Displays filterable, searchable list of notes
 * Shows preview of note content with tags
 */

import type { Note } from '@/pages/Notes';
import './NoteList.css';

interface NoteListProps {
  notes: Note[];
  onNoteClick: (note: Note) => void;
  onTogglePin: (noteId: string) => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
}

function getPreview(content: string): string {
  const stripped = content
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/`/g, '')
    .replace(/\n/g, ' ')
    .trim();
  return stripped.length > 100 ? stripped.slice(0, 100) + '...' : stripped;
}

export function NoteList({ notes, onNoteClick, onTogglePin }: NoteListProps): JSX.Element {
  if (notes.length === 0) {
    return (
      <div className="note-list note-list--empty">
        <div className="note-list__empty-icon">📝</div>
        <p className="note-list__empty-text">No notes found</p>
        <p className="note-list__empty-hint">Create a new note or adjust your filters</p>
      </div>
    );
  }

  return (
    <div className="note-list">
      {notes.map((note) => (
        <div
          key={note.id}
          className="note-card"
          onClick={() => onNoteClick(note)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onNoteClick(note)}
        >
          <div className="note-card__header">
            <h2 className="note-card__title">{note.title || 'Untitled'}</h2>
            <button
              type="button"
              className={`note-card__pin ${note.pinned ? 'note-card__pin--active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(note.id);
              }}
              aria-label={note.pinned ? 'Unpin note' : 'Pin note'}
            >
              📌
            </button>
          </div>

          <p className="note-card__preview">{getPreview(note.content)}</p>

          <div className="note-card__footer">
            <span className="note-card__date">{formatDate(note.updated_at)}</span>
            {note.tags.length > 0 && (
              <div className="note-card__tags">
                {note.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="note-card__tag">
                    {tag}
                  </span>
                ))}
                {note.tags.length > 3 && (
                  <span className="note-card__tag note-card__tag--more">
                    +{note.tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
